import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LoadingState } from '../ui/LoadingState.js';
import { Card, CardTitle } from '../ui/Card.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Dropdown } from '../ui/Dropdown.js';
import { EstimateDraftCardConnected } from './EstimateDraftCardConnected.js';
import { EstimateReplyCardConnected } from './EstimateReplyCardConnected.js';
import { useEstimateDrafts, useEstimateReplies } from '../../hooks/useEstimateOutreach.js';
import { Select } from '../ui/Select.js';
import { Button } from '../ui/Button.js';
import { type PillTone } from '../PillGroup.js';
import { formatMoney } from '../../lib/money.js';
import { previewTotals } from '../../lib/billingTotals.js';
import { useContactProjects } from '../../hooks/useProjects.js';
import { useDebouncedFlush } from '../../hooks/useDebouncedFlush.js';
import { SettingsLink } from '../ui/SettingsLink.js';
import {
  ConceptEditor, ConceptPreviewToggle, useConceptPreview,
  EMPTY_LINE, linesFromItems, itemsFromLines, previewItems,
  type LineItemForm,
} from './ConceptEditor.js';
import { useOpportunityByEstimate } from '../../hooks/useFunnels.js';
import {
  useEstimate,
  useCreateEstimate,
  useUpdateEstimate,
  useInvoices,
  type EstimateLineItem,
  type BillingAccount,
} from '../../hooks/useBilling.js';

// Columnas de la tabla de conceptos — las del diseño. UNA constante: cabecera
// y filas se desalinean si difieren. La quinta columna (32px) solo existe en
// modo draft para el botón de quitar línea.
export const ITEM_COLS = '1fr 60px 110px 120px';
export const ITEM_COLS_DRAFT = '1fr 60px 110px 120px 32px';

export const ESTIMATE_STATUS_TONES: Record<string, PillTone> = {
  draft: 'neutral',
  sent: 'cyan',
  accepted: 'success',
  declined: 'danger',
  expired: 'muted',
  converted: 'info',
  // Invoice statuses — same map, keyed by status string (BillingPage list +
  // DetailHeader). 'overdue' es derivado en UI (billingStatus.ts), no en DB.
  paid: 'success',
  overdue: 'danger',
  void: 'muted',
};


function formatDocDate(value: number): string {
  return new Date(value).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Piezas compartidas del layout de documento (usadas también por InvoiceDetail) ──

/** Cabecera de la tabla de conceptos — caps del diseño. */
export function ConceptTableHead({ cols }: { cols: string }) {
  const { t } = useTranslation();
  const th = (label: string, right?: boolean) => (
    <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)', fontFamily: 'var(--evo-font)', ...(right ? { textAlign: 'right' as const } : {}) }}>
      {label}
    </span>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 'var(--evo-space-2)', paddingBottom: 8, borderBottom: '1px solid var(--evo-stroke)' }}>
      {th(t('billing.estimates.description'))}
      {th(t('billing.estimates.qty'), true)}
      {th(t('billing.estimates.unitPrice'), true)}
      {th(t('billing.estimates.amount'), true)}
    </div>
  );
}

/** Fila de concepto en solo lectura — tipografía y grid del diseño. */
export function ConceptRow({ item, currency, last }: { item: EstimateLineItem; currency: string; last: boolean }) {
  if (item.kind === 'note') {
    return (
      <div style={{ padding: 'var(--evo-detail-kv-row-py) 0', borderBottom: last ? 'none' : '1px solid var(--evo-stroke)', minHeight: 'var(--evo-detail-kv-row-min-h)', boxSizing: 'border-box' }}>
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {item.description}
        </p>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: ITEM_COLS, gap: 'var(--evo-space-2)', alignItems: 'center', padding: 'var(--evo-detail-kv-row-py) 0', borderBottom: last ? 'none' : '1px solid var(--evo-stroke)', minHeight: 'var(--evo-detail-kv-row-min-h)', boxSizing: 'border-box' }}>
      <span style={{ minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-text-strong)', fontFamily: 'var(--evo-font)', wordBreak: 'break-word' }}>{item.description}</span>
      <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', textAlign: 'right' }}>{item.qty}</span>
      <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', textAlign: 'right' }}>{formatMoney(item.unitPrice, currency)}</span>
      <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-text-strong)', fontFamily: 'var(--evo-font)', textAlign: 'right' }}>{formatMoney(item.qty * item.unitPrice, currency)}</span>
    </div>
  );
}

/** Fila clave→valor del documento: la etiqueta toma su ancho natural y el valor
 * queda a la IZQUIERDA, pegado a ella. KVRow alinea el valor a la derecha; el
 * handoff de Facturación no.
 * El ancho fijo de etiqueta se retiró del design system en eea71b1 (desperdiciaba
 * con etiquetas cortas, truncaba con largas), así que acá no se reintroduce. */
export function DocKV({ label, children, last }: { label: string; children?: ReactNode; last?: boolean }) {
  const empty = children == null || children === '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', padding: 'var(--evo-detail-kv-row-py) 0', borderBottom: last ? 'none' : '1px solid var(--evo-stroke)', minHeight: 'var(--evo-detail-kv-row-min-h)', boxSizing: 'border-box' }}>
      <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-text-strong)', fontFamily: 'var(--evo-font)', wordBreak: 'break-word' }}>
        {empty ? <span style={{ color: 'var(--evo-text-muted)', fontStyle: 'italic' }}>—</span> : children}
      </span>
    </div>
  );
}

/** Total destacado del diseño: strong en cian + moneda. */
export function TotalValue({ amount, currency }: { amount: number; currency: string }) {
  return (
    <strong style={{ fontWeight: 600, color: 'var(--evo-cyan)' }}>
      {formatMoney(amount, currency)} {currency.toUpperCase()}
    </strong>
  );
}

export interface EstimateDetailProps {
  id: number | null;
  accounts: BillingAccount[];
  /** Nombre del cliente para la tarjeta Documento (el caller lo resuelve). */
  clientName?: string | undefined;
  /**
   * Only needed to CREATE a new estimate (id === null) — there's no loaded
   * estimate yet to read them from. When viewing/editing an existing
   * estimate (id !== null), contactId/projectId are read from the loaded
   * estimate itself, so these props aren't required from that context (e.g.
   * the standalone billing module, which doesn't have a "current project").
   */
  projectId?: number;
  contactId?: number;
}

// Body-only — no title, status pill, or header chrome. Those differ by
// caller context and are rendered by each caller around this.
// Layout del diseño: duo [Documento | Totales] + Conceptos + Notas, todo en
// tarjetas Card con cabecera caps. En draft los campos son editables dentro
// de las MISMAS tarjetas — no hay un "modo formulario" aparte ni pestañas.
export function EstimateDetail({ id, projectId, contactId, accounts, clientName }: EstimateDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: estimate, isLoading } = useEstimate(id ?? undefined);
  const { data: draftsData } = useEstimateDrafts(id);
  const { data: repliesData } = useEstimateReplies(id);
  const drafts = draftsData?.drafts ?? [];
  const replies = repliesData?.replies ?? [];
  const createEstimate = useCreateEstimate();
  const updateEstimate = useUpdateEstimate();
  // Sentinel (-1) when id is null — never matches a real invoice, avoids an
  // unconditional fetch-all-invoices call from a brand-new create form.
  const { data: convertedInvoicesData } = useInvoices({ estimateId: id ?? -1 });
  const convertedInvoice = convertedInvoicesData?.invoices[0];

  const [accountId, setAccountId] = useState<number | null>(accounts[0]?.id ?? null);
  const [linkedProjectId, setLinkedProjectId] = useState<number | null>(projectId ?? null);
  // Enlace de vuelta a la oportunidad que originó la cotización. Sin esto el
  // vínculo existe solo del lado del tablero y desde acá no se encuentra.
  const { data: opportunityLink } = useOpportunityByEstimate(id);
  const [lines, setLines] = useState<LineItemForm[]>([{ ...EMPTY_LINE }]);
  const [taxPercent, setTaxPercent] = useState('0');
  const [discount, setDiscount] = useState('');
  const [discountLabel, setDiscountLabel] = useState('');
  const [deposit, setDeposit] = useState('');
  const [notes, setNotes] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [saved, setSaved] = useState(false);
  // Ver cómo queda sin tener que guardar. La tabla de conceptos es lo único
  // del documento que se edita en una forma distinta a como se imprime, así
  // que es lo único que necesita una vista previa.
  const { preview: previewConcepts, toggle: togglePreviewConcepts } = useConceptPreview();
  // Lo último que se mandó, serializado. null = todavía no se cargó el documento.
  const ultimoGuardado = useRef<string | null>(null);

  // id !== null implies `estimate` is loaded by the time this is read for a
  // save — the component returns a LoadingState below before that point.
  // id === null (create) has no loaded estimate, so contactId comes from
  // the prop there (matching the existing account/create pattern).
  const resolvedContactId = estimate ? estimate.contactId : contactId;
  const { data: projectsData } = useContactProjects(resolvedContactId);
  const contactProjects = projectsData?.items ?? [];

  useEffect(() => {
    if (!estimate) return;
    setAccountId(estimate.accountId);
    setLinkedProjectId(estimate.projectId);
    ultimoGuardado.current = null;
    setLines(linesFromItems(JSON.parse(estimate.lineItemsJson) as EstimateLineItem[]));
    setTaxPercent((estimate.taxRate / 100).toString());
    setDiscount(estimate.discountAmount ? (estimate.discountAmount / 100).toFixed(2) : '');
    setDiscountLabel(estimate.discountLabel ?? '');
    setDeposit(estimate.depositPercent != null ? String(estimate.depositPercent) : '');
    setNotes(estimate.notes ?? '');
    setValidUntil(estimate.validUntil ?? '');
  }, [estimate]);

  /**
   * ¿Se puede corregir todavía?
   *
   * Hasta el 2026-09-16 esto era `status === 'draft'` y se llamaba `esEditable`:
   * enviar congelaba el documento. El caso frecuente es el contrario —el
   * cliente mira la cotización y PIDE un ajuste— y rehacerla entera para mover
   * un renglón es trabajo que nadie debería hacer.
   *
   * Una ACEPTADA sí se congela, y ahí el motivo es otro: alguien dijo que sí a
   * ese monto. Para eso está el botón de duplicar.
   */
  const esEditable = !estimate || estimate.status === 'draft' || estimate.status === 'sent';
  const account = accounts.find(a => a.id === accountId);
  const currency = account?.currency ?? 'usd';
  const preview = previewTotals(lines, taxPercent, discount, deposit);
  const savedItems: EstimateLineItem[] = estimate ? (JSON.parse(estimate.lineItemsJson) as EstimateLineItem[]) : [];

  /** Lo editable del documento, tal como se manda. null = todavía no se puede. */
  function armarPayload() {
    if (!accountId) return null;
    if (resolvedContactId === undefined) return null;
    return {
      accountId,
      contactId: resolvedContactId,
      projectId: linkedProjectId,
      lineItems: itemsFromLines(lines),
      taxRate: Math.max(0, Math.round((Number(taxPercent) || 0) * 100)),
      discountAmount: Math.max(0, Math.round((Number(discount) || 0) * 100)),
      discountLabel: discountLabel.trim() || null,
      depositPercent: Math.min(100, Math.max(0, Math.trunc(Number(deposit) || 0))) || null,
      notes: notes.trim() || null,
      validUntil: validUntil || null,
    };
  }

  function handleSave() {
    const payload = armarPayload();
    if (!payload) return;
    if (id === null) {
      createEstimate.mutate(payload, { onSuccess: marcarGuardado });
    } else {
      ultimoGuardado.current = JSON.stringify(payload);
      updateEstimate.mutate({ id, ...payload }, { onSuccess: marcarGuardado });
    }
  }

  function marcarGuardado() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  // ── Autoguardado ───────────────────────────────────────────────────────────
  //
  // Un documento que YA existe se guarda solo: editar y salir de la pantalla no
  // puede perder lo que se acaba de escribir. Solo con `id` —el alta necesita
  // que alguien apriete Crear, si no cada tecla dejaría un borrador nuevo— y
  // solo en borrador, que es lo único editable.
  //
  // La comparación contra lo último guardado es lo que evita el guardado
  // fantasma: al cargar el documento el efecto corre igual, y sin esto
  // escribiría en la base sin que nadie tocara nada.
  const autoguardado = useDebouncedFlush(() => { handleSave(); }, 900, true);

  useEffect(() => {
    if (id === null || !esEditable || updateEstimate.isPending) return;
    const payload = armarPayload();
    if (!payload) return;
    const serializado = JSON.stringify(payload);
    if (ultimoGuardado.current === null) { ultimoGuardado.current = serializado; return; }
    if (ultimoGuardado.current === serializado) return;
    autoguardado.queue();
  // `autoguardado` es estable entre renders; incluirlo dispararía el efecto en
  // cada cambio de estado de la mutación.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, esEditable, updateEstimate.isPending, accountId, resolvedContactId, linkedProjectId,
      lines, taxPercent, discount, discountLabel, deposit, notes, validUntil]);

  if (id !== null && isLoading) return <LoadingState size="sm" />;

  const isPending = createEstimate.isPending || updateEstimate.isPending;
  const taxLabel = `${t('billing.estimates.tax')} (${(Number(taxPercent) || 0)}%)`;
  const conceptCols = esEditable ? ITEM_COLS_DRAFT : ITEM_COLS;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      {estimate?.status === 'converted' && convertedInvoice && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-3)', background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>
            {t('billing.facturacion.convertedInfo', { number: convertedInvoice.number ?? `#${convertedInvoice.id}` })}
          </span>
          <Button size="sm" variant="outline" tone="cyan" onClick={() => navigate(`/facturacion/invoice/${convertedInvoice.id}`)}>
            {t('billing.facturacion.viewInvoice')}
          </Button>
        </div>
      )}

      {/* Duo del diseño: Documento | Totales */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--evo-space-4)', alignItems: 'stretch' }}>
        <Card header={<CardTitle>{t('billing.doc.document')}</CardTitle>}>
          <DocKV label={t('billing.doc.folio')}>{estimate ? (estimate.number ?? `#${estimate.id}`) : undefined}</DocKV>
          <DocKV label={t('billing.doc.client')}>{clientName}</DocKV>
          {opportunityLink?.opportunity && (
            <DocKV label={t('opportunities.backlink')}>
              <Button
                size="sm"
                variant="link"
                tone="cyan"
                onClick={() => navigate(`/embudos?funnel=${opportunityLink.opportunity!.funnelId}`)}
              >
                {opportunityLink.opportunity.title}
              </Button>
            </DocKV>
          )}
          {estimate && <DocKV label={t('billing.doc.issued')}>{formatDocDate(estimate.createdAt)}</DocKV>}
          {/* Cuenta y proyecto solo se muestran mientras se puede editar —
              el documento de lectura lleva las cuatro filas del diseño. */}
          {esEditable && (
            <DocKV label={t('billing.estimates.account')}>
              {accounts.length === 0 ? (
                <span className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>
                  {t('billing.estimates.noAccounts', { tab: '' }).trim()} <SettingsLink tab="data" />
                </span>
              ) : (
                <Dropdown<number>
                  value={accountId ?? accounts[0]!.id}
                  options={accounts.map(a => ({ value: a.id, label: `${a.name} (${a.currency.toUpperCase()})` }))}
                  onChange={v => { setAccountId(v); setSaved(false); }}
                  size="sm"
                />
              )}
            </DocKV>
          )}
          {esEditable && (
            <DocKV label={t('billing.facturacion.project')}>
              <Select<number>
                value={linkedProjectId}
                onChange={setLinkedProjectId}
                options={contactProjects.map(p => ({ value: p.id, label: p.name }))}
                placeholder={contactProjects.length === 0 ? t('billing.facturacion.projectNone') : t('billing.facturacion.projectPlaceholder')}
                clearable
                size="sm"
              />
            </DocKV>
          )}
          <DocKV label={t('billing.estimates.validUntil')}>
            {esEditable ? (
              <Input type="date" value={validUntil} onChange={setValidUntil} size="sm" />
            ) : (
              validUntil || undefined
            )}
          </DocKV>
          {/* Idioma del documento: a un cliente de Florida se le
              factura en inglés y a uno de Santo Domingo en español,
              desde la misma cuenta. Vacío = el de la cuenta.
              Solo con documento guardado: en el alta todavía no hay a qué
              cotización aplicárselo. */}
          {estimate && id !== null && (
          <DocKV label={t('billing.doc.language')} last>
            <Dropdown<string>
              value={estimate.language ?? 'account'}
              options={[
                { value: 'account', label: t('billing.doc.languageAccount') },
                { value: 'es', label: t('settings.generalSection.langEs') },
                { value: 'en', label: t('settings.generalSection.langEn') },
              ]}
              onChange={v => updateEstimate.mutate({ id, language: v === 'account' ? null : v })}
              size="sm"
            />
          </DocKV>
          )}
        </Card>

        <Card header={<CardTitle>{t('billing.doc.totals')}</CardTitle>}>
          <DocKV label={t('billing.estimates.subtotal')}>{formatMoney(preview.subtotal, currency)}</DocKV>
          {esEditable ? (
            <DocKV label={t('billing.doc.discount')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                <span style={{ width: 90 }}>
                  <Input type="number" value={discount} onChange={v => { setDiscount(v); setSaved(false); }} size="sm" fullWidth />
                </span>
                <span style={{ width: 130 }}>
                  <Input value={discountLabel} onChange={v => { setDiscountLabel(v); setSaved(false); }} placeholder={t('billing.doc.discountReason')} size="sm" fullWidth />
                </span>
              </span>
            </DocKV>
          ) : preview.discountAmount > 0 ? (
            <DocKV label={discountLabel ? `${t('billing.doc.discount')} · ${discountLabel}` : t('billing.doc.discount')}>
              {`−${formatMoney(preview.discountAmount, currency)}`}
            </DocKV>
          ) : null}
          <DocKV label={esEditable ? t('billing.estimates.taxRatePercent') : taxLabel}>
            {esEditable ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                <span style={{ width: 70 }}>
                  <Input type="number" value={taxPercent} onChange={v => { setTaxPercent(v); setSaved(false); }} size="sm" fullWidth />
                </span>
                {formatMoney(preview.taxAmount, currency)}
              </span>
            ) : (
              formatMoney(preview.taxAmount, currency)
            )}
          </DocKV>
          <DocKV label={t('billing.estimates.total')}>
            <TotalValue amount={preview.total} currency={currency} />
          </DocKV>
          {/* Anticipo: cuánto tiene que entrar para arrancar. Va debajo del
              total porque se calcula sobre él. */}
          {esEditable ? (
            <DocKV label={t('billing.doc.deposit')} last>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                <span style={{ width: 70 }}>
                  <Input type="number" value={deposit} onChange={v => { setDeposit(v); setSaved(false); }} size="sm" fullWidth />
                </span>
                {preview.depositAmount != null ? formatMoney(preview.depositAmount, currency) : '%'}
              </span>
            </DocKV>
          ) : preview.depositAmount != null ? (
            <DocKV label={`${t('billing.doc.deposit')} · ${preview.depositPercent}%`} last>
              {formatMoney(preview.depositAmount, currency)}
            </DocKV>
          ) : null}
        </Card>
      </div>

      {/* Los correos de ESTA cotización, donde se los busca. */}
      {id !== null && (drafts.length > 0 || replies.length > 0) && (
        <Card header={<CardTitle>{t('billing.doc.emails')}</CardTitle>} padding="md">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            {drafts.map(d => (
              <EstimateDraftCardConnected key={d.id} draft={d} estimateId={id} />
            ))}
            {replies.map(r => (
              <EstimateReplyCardConnected key={r.id} reply={r} estimateId={id} />
            ))}
          </div>
        </Card>
      )}

      <Card
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
            <div style={{ flex: 1, minWidth: 0 }}><CardTitle>{t('billing.doc.concepts')}</CardTitle></div>
            {esEditable && <ConceptPreviewToggle preview={previewConcepts} onToggle={togglePreviewConcepts} />}
          </div>
        }
      >
        <ConceptTableHead cols={conceptCols} />
        {esEditable && !previewConcepts ? (
          <ConceptEditor lines={lines} onChange={ls => { setLines(ls); setSaved(false); }} currency={currency} cols={conceptCols} />
        ) : (
          // En vista previa se dibuja lo que hay EN EL FORMULARIO, no lo
          // guardado: la gracia es ver el cambio antes de guardarlo.
          (esEditable ? previewItems(lines) : savedItems).map((item, idx, arr) => (
            <ConceptRow key={idx} item={item} currency={currency} last={idx === arr.length - 1} />
          ))
        )}
      </Card>

      <Card header={<CardTitle>{t('billing.estimates.notes')}</CardTitle>}>
        {esEditable ? (
          <Textarea value={notes} onChange={v => { setNotes(v); setSaved(false); }} rows={3} fullWidth />
        ) : (
          <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.6 }}>
            {notes || '—'}
          </p>
        )}
      </Card>

      {esEditable && (
        <div>
          <Button
            tone="cyan"
            variant={saved ? 'outline' : 'solid'}
            size="sm"
            loading={isPending}
            disabled={!accountId}
            onClick={handleSave}
          >
            {isPending ? t('billing.estimates.saving') : saved ? t('billing.estimates.saved') : id === null ? t('billing.estimates.createBtn') : t('billing.estimates.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
